import React, { FC, useEffect, useState } from "react";
import { Skeleton } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { PropertiesWithConfig, SalesItemTranslated } from "@/types";
import { useTranslation } from "react-i18next";
import NumberInput from "@/components/atoms/NumberInput";
import FilterCheckboxList from "@/components/molecules/FilterCheckboxList";
import FilterPanel from "@/components/molecules/FilterPanel";
import FilterSection from "@/components/molecules/FilterSection";

interface FilterPanelProps {
  salesItems: SalesItemTranslated[] | undefined;
  isLoadingSalesItems: boolean;
  selectedSalesItemId: number | null;
  onSalesItemChange: (salesItemId: number) => void;

  dynamicProperties: PropertiesWithConfig[] | undefined;
  isLoadingProperties: boolean;
  selectedPropertyIds: number[];
  onPropertyChange: (propertyId: number) => void;

  selectedOrderTypes: number[];
  onOrderTypeChange: (orderTypeId: number) => void;

  minAmount: number | undefined;
  setMinAmount: (value: number | undefined) => void;
  maxAmount: number | undefined;
  setMaxAmount: (value: number | undefined) => void;
  minPrice: number | undefined;
  setMinPrice: (value: number | undefined) => void;
  maxPrice: number | undefined;
  setMaxPrice: (value: number | undefined) => void;

  onClearAll: () => void;
  className?: string;
}

const MarketFilterPanel: FC<FilterPanelProps> = ({
  salesItems,
  isLoadingSalesItems,
  selectedSalesItemId,
  onSalesItemChange,
  dynamicProperties,
  isLoadingProperties,
  selectedPropertyIds,
  onPropertyChange,
  selectedOrderTypes,
  onOrderTypeChange,
  minAmount,
  setMinAmount,
  maxAmount,
  setMaxAmount,
  minPrice,
  setMinPrice: _setMinPrice,
  maxPrice,
  setMaxPrice: _setMaxPrice,
  onClearAll,
  className,
}) => {
  const { t } = useTranslation();

  // Local state for number inputs - debounced before propagating to parent
  const [localMinAmount, setLocalMinAmount] = useState<number | string | undefined>(minAmount);
  const [localMaxAmount, setLocalMaxAmount] = useState<number | string | undefined>(maxAmount);
  // const [localMinPrice, setLocalMinPrice] = useState<number | string | undefined>(minPrice);
  // const [localMaxPrice, setLocalMaxPrice] = useState<number | string | undefined>(maxPrice);

  const [debouncedMinAmount] = useDebouncedValue(localMinAmount, 500);
  const [debouncedMaxAmount] = useDebouncedValue(localMaxAmount, 500);
  // const [debouncedMinPrice] = useDebouncedValue(localMinPrice, 500);
  // const [debouncedMaxPrice] = useDebouncedValue(localMaxPrice, 500);

  // Propagate debounced values to parent
  useEffect(() => {
    setMinAmount(debouncedMinAmount === "" ? undefined : (debouncedMinAmount as number | undefined));
  }, [debouncedMinAmount]);
  useEffect(() => {
    setMaxAmount(debouncedMaxAmount === "" ? undefined : (debouncedMaxAmount as number | undefined));
  }, [debouncedMaxAmount]);
  // useEffect(() => { setMinPrice(debouncedMinPrice === "" ? undefined : debouncedMinPrice as number | undefined); }, [debouncedMinPrice]);
  // useEffect(() => { setMaxPrice(debouncedMaxPrice === "" ? undefined : debouncedMaxPrice as number | undefined); }, [debouncedMaxPrice]);

  // Sync local state when parent clears (e.g. clear all button)
  useEffect(() => {
    if (minAmount === undefined) setLocalMinAmount(undefined);
  }, [minAmount]);
  useEffect(() => {
    if (maxAmount === undefined) setLocalMaxAmount(undefined);
  }, [maxAmount]);
  // useEffect(() => { if (minPrice === undefined) setLocalMinPrice(undefined); }, [minPrice]);
  // useEffect(() => { if (maxPrice === undefined) setLocalMaxPrice(undefined); }, [maxPrice]);

  const orderTypeOptions = [
    { id: 1, label: t("actions:buy") },
    { id: 2, label: t("actions:sell") },
  ];

  // The product type is left out on purpose: the market always keeps one product type selected (Plastic
  // by default) and onClearAll does not reset it, so it never counts as something to clear. This differs
  // from the orders filter, where the product type is optional and part of "Reset".
  const hasActiveFilters =
    selectedPropertyIds.length > 0 ||
    selectedOrderTypes.length > 0 ||
    minAmount !== undefined ||
    maxAmount !== undefined ||
    minPrice !== undefined ||
    maxPrice !== undefined;

  return (
    <FilterPanel
      onReset={onClearAll}
      resetDisabled={!hasActiveFilters}
      defaultOpen={["salesItems", "orderType", "amount", "price"]}
      className={className}
      resetTestId="market-filter-reset"
    >
      <FilterSection value="salesItems" title={t("product:productType")}>
        {isLoadingSalesItems ? (
          <Skeleton height={200} />
        ) : (
          <FilterCheckboxList
            ariaLabel={t("product:productType")}
            options={(salesItems ?? []).map((item) => ({
              value: String(item.salesItemId),
              label: item.salesItemTranslated,
            }))}
            selected={selectedSalesItemId === null ? [] : [String(selectedSalesItemId)]}
            onToggle={(value) => onSalesItemChange(Number(value))}
          />
        )}
      </FilterSection>

      <FilterSection value="orderType" title={t("commonOrders:buyOrSell")}>
        <FilterCheckboxList
          ariaLabel={t("commonOrders:buyOrSell")}
          options={orderTypeOptions.map((orderType) => ({ value: String(orderType.id), label: orderType.label }))}
          selected={selectedOrderTypes.map(String)}
          onToggle={(value) => onOrderTypeChange(Number(value))}
        />
      </FilterSection>

      <FilterSection value="amount" title={t("common:amount")}>
        <NumberInput
          label={"Min"}
          placeholder={"Min"}
          value={localMinAmount}
          onChange={(value) => setLocalMinAmount(value === "" ? undefined : Number(value))}
          min={0}
          styles={{ root: { marginBottom: "0.75rem" } }}
        />
        <NumberInput
          label={"Max"}
          placeholder={"Max"}
          value={localMaxAmount}
          onChange={(value) => setLocalMaxAmount(value === "" ? undefined : Number(value))}
          min={0}
        />
      </FilterSection>

      {/* <FilterSection value="price" title={t("common:price")}>
        <NumberInput
          label={t("orders:from")}
          placeholder={t("orders:from")}
          value={localMinPrice}
          onChange={(value) => setLocalMinPrice(value === "" ? undefined : Number(value))}
          min={0}
          styles={{ root: { marginBottom: "0.75rem" } }}
        />
        <NumberInput
          label={t("orders:to")}
          placeholder={t("orders:to")}
          value={localMaxPrice}
          onChange={(value) => setLocalMaxPrice(value === "" ? undefined : Number(value))}
          min={0}
        />
      </FilterSection> */}

      {/* Dynamic Property Sections - Each propConfig gets its own accordion */}
      {selectedSalesItemId &&
        (isLoadingProperties ? (
          <FilterSection value="propertiesLoading" title={t("common:properties")}>
            <Skeleton height={150} />
          </FilterSection>
        ) : (
          dynamicProperties?.map((propConfig) => (
            <FilterSection
              key={propConfig.config.translatedLabel}
              value={`property_${propConfig.config.translatedLabel}`}
              title={propConfig.config.translatedLabel}
            >
              <FilterCheckboxList
                ariaLabel={propConfig.config.translatedLabel}
                options={propConfig.properties.map((prop) => ({ value: String(prop.id), label: prop.value }))}
                selected={selectedPropertyIds.map(String)}
                onToggle={(value) => onPropertyChange(Number(value))}
              />
            </FilterSection>
          ))
        ))}
    </FilterPanel>
  );
};

export default MarketFilterPanel;
